import { normalizeCustomer, normalizeJob, normalizeRegularInspection } from './api';
import { getApiAccessToken } from './entraAuth';

const ENV = {
  REACT_APP_CRM_API_URL: process.env.REACT_APP_CRM_API_URL,
  REACT_APP_CRM_API_ACCESS_KEY: process.env.REACT_APP_CRM_API_ACCESS_KEY,
  REACT_APP_CRM_API_ZAKAZKY_LIST_PATH: process.env.REACT_APP_CRM_API_ZAKAZKY_LIST_PATH,
  REACT_APP_CRM_ALL_TECH_EMAILS: process.env.REACT_APP_CRM_ALL_TECH_EMAILS,
  REACT_APP_CRM_API_ZAKAZNICI_LIST_PATH: process.env.REACT_APP_CRM_API_ZAKAZNICI_LIST_PATH,
  REACT_APP_CRM_API_ZAKAZKY_PATH: process.env.REACT_APP_CRM_API_ZAKAZKY_PATH,
  REACT_APP_CRM_API_ZAKAZNICI_PATH: process.env.REACT_APP_CRM_API_ZAKAZNICI_PATH,
  REACT_APP_CRM_API_PROVIZE_ZAMESTNANEC_PATH: process.env.REACT_APP_CRM_API_PROVIZE_ZAMESTNANEC_PATH,
  REACT_APP_CRM_API_PRAVIDELNE_KONTROLY_PATH: process.env.REACT_APP_CRM_API_PRAVIDELNE_KONTROLY_PATH,
  REACT_APP_CRM_API_AI_VOICE_DRAFT_PATH: process.env.REACT_APP_CRM_API_AI_VOICE_DRAFT_PATH,
  REACT_APP_CRM_API_AI_AGENT_PATH: process.env.REACT_APP_CRM_API_AI_AGENT_PATH,
  REACT_APP_CRM_API_AI_AGENT_SESSION_PATH: process.env.REACT_APP_CRM_API_AI_AGENT_SESSION_PATH,
  REACT_APP_CRM_API_SCAN_PROTOCOL_AI_PATH: process.env.REACT_APP_CRM_API_SCAN_PROTOCOL_AI_PATH,
  REACT_APP_CRM_API_SCAN_PROTOCOL_UPLOAD_PATH: process.env.REACT_APP_CRM_API_SCAN_PROTOCOL_UPLOAD_PATH,
  REACT_APP_CRM_API_SCAN_PROTOCOL_ENQUEUE_PATH: process.env.REACT_APP_CRM_API_SCAN_PROTOCOL_ENQUEUE_PATH,
  REACT_APP_CRM_API_SCAN_PROTOCOL_QUEUE_LIST_PATH: process.env.REACT_APP_CRM_API_SCAN_PROTOCOL_QUEUE_LIST_PATH,
  REACT_APP_CRM_API_SCAN_PROTOCOL_PROCESS_PATH: process.env.REACT_APP_CRM_API_SCAN_PROTOCOL_PROCESS_PATH,
  REACT_APP_CRM_API_SCAN_PROTOCOL_MANAGE_PATH: process.env.REACT_APP_CRM_API_SCAN_PROTOCOL_MANAGE_PATH,
  REACT_APP_CRM_API_VYFAKTURUJ_UNPAID_PATH: process.env.REACT_APP_CRM_API_VYFAKTURUJ_UNPAID_PATH,
  REACT_APP_CRM_API_INSOLVENCY_PATH: process.env.REACT_APP_CRM_API_INSOLVENCY_PATH,
};

function required(name) {
  const value = ENV[name];
  if (!value || !String(value).trim()) {
    throw new Error(`Missing required env var: ${name}`);
  }
  return value;
}

function getBaseUrl() {
  const base = required('REACT_APP_CRM_API_URL');
  return base.endsWith('/') ? base : `${base}/`;
}

function getApiKey() {
  return required('REACT_APP_CRM_API_ACCESS_KEY');
}

function hasJwtShape(value) {
  return typeof value === 'string' && value.split('.').length === 3;
}

async function getAuthorizationHeader() {
  try {
    const accessToken = await getApiAccessToken();
    if (hasJwtShape(accessToken)) {
      return `Bearer ${accessToken}`;
    }
  } catch {
    // Fallback to legacy API key mode when Entra token is not available.
  }

  return `Bearer ${getApiKey()}`;
}

function getPath(name, fallback) {
  const value = ENV[name];
  if (!value || !String(value).trim()) return fallback;
  return String(value).replace(/^\/+/, '');
}

function buildQuery(params) {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    search.set(key, String(value));
  });
  const text = search.toString();
  return text ? `?${text}` : '';
}

function unwrapListResponse(raw) {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object' && Array.isArray(raw.items)) return raw.items;
  return [];
}

function wait(ms, signal) {
  return new Promise((resolve, reject) => {
    if (!ms || ms <= 0) {
      resolve();
      return;
    }

    const timer = setTimeout(() => {
      if (signal) {
        signal.removeEventListener('abort', onAbort);
      }
      resolve();
    }, ms);

    const onAbort = () => {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    };

    if (signal) {
      if (signal.aborted) {
        onAbort();
        return;
      }
      signal.addEventListener('abort', onAbort, { once: true });
    }
  });
}

function parseRetryAfterMs(res) {
  const value = (res && res.headers && res.headers.get('retry-after')) || '';
  const asNumber = Number(value);
  if (Number.isFinite(asNumber) && asNumber > 0) {
    return Math.round(asNumber * 1000);
  }
  return null;
}

function unwrapPagedResponse(raw, page, pageSize) {
  const items = unwrapListResponse(raw).map((item) => normalizeJob(item));
  const totalCandidates = [
    raw && raw.total,
    raw && raw.totalCount,
    raw && raw.count,
  ];
  const total = totalCandidates.find((value) => Number.isFinite(Number(value)));
  const numericTotal = total === undefined ? null : Number(total);
  const explicitHasMore = raw && typeof raw === 'object' ? raw.hasMore : undefined;
  const hasMore =
    typeof explicitHasMore === 'boolean'
      ? explicitHasMore
      : numericTotal !== null
        ? page * pageSize < numericTotal
        : items.length >= pageSize;

  return {
    items,
    total: numericTotal,
    hasMore,
    scope: raw && typeof raw === 'object' ? raw.scope : undefined,
    canViewAll: raw && typeof raw === 'object' ? Boolean(raw.canViewAll) : false,
  };
}

async function request(path, options) {
  const url = `${getBaseUrl()}${path}`;
  const requestOptions = options || {};
  const authorization = await getAuthorizationHeader();
  const maxAttempts = 4;
  const method = (requestOptions.method ? String(requestOptions.method) : 'GET').toUpperCase();
  const isSafeRetry = method === 'GET';
  const signal = requestOptions.signal;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    const headers = {
      Authorization: authorization,
      ...(requestOptions.headers ? requestOptions.headers : {}),
    };

    const isFormDataBody = typeof FormData !== 'undefined' && requestOptions.body instanceof FormData;
    if (!isFormDataBody && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }

    const res = await fetch(url, {
      ...requestOptions,
      headers,
    });

    if (res.status === 429 && isSafeRetry && attempt < maxAttempts) {
      const retryAfterMs = parseRetryAfterMs(res);
      const backoffMs = retryAfterMs || 300 * Math.pow(2, attempt - 1);
      await wait(backoffMs, signal);
      continue;
    }

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      let errorMessage = text || `HTTP ${res.status}`;

      if (text) {
        try {
          const parsed = JSON.parse(text);
          if (parsed && typeof parsed === 'object' && parsed.message) {
            errorMessage = String(parsed.message);
          }
        } catch {
          // Keep original text when body is not JSON.
        }
      }

      const err = new Error(errorMessage);
      err.status = res.status;
      err.rawBody = text;
      throw err;
    }

    const contentType = res.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) return null;
    return res.json().catch(() => null);
  }

  const err = new Error('HTTP 429');
  err.status = 429;
  throw err;
}

function isForbidden(error) {
  const status = error && typeof error === 'object' ? error.status : undefined;
  const message = error instanceof Error ? error.message : '';
  return status === 403 || /\b403\b/.test(String(message || ''));
}

function isBadRequest(error) {
  const status = error && typeof error === 'object' ? error.status : undefined;
  const message = error instanceof Error ? error.message : '';
  return status === 400 || /\b400\b/.test(String(message || ''));
}

async function fetchJobsEnhanced({ email, technicianEmail, page, pageSize, sortBy, sortDir, includeTotal, signal } = {}) {
  const path = getPath('REACT_APP_CRM_API_ZAKAZKY_LIST_PATH', 'protokoly-zakazky-list-entra');
  const normalizedEmail = String(email || '').trim();
  const normalizedTechnicianEmail = String(technicianEmail || '').trim();
  const query = buildQuery({
    email: normalizedEmail,
    technicianEmail: normalizedTechnicianEmail,
    page,
    pageSize,
    sortBy,
    sortDir,
    includeTotal,
  });
  const raw = await request(`${path}${query}`, { method: 'GET', signal });
  return unwrapListResponse(raw).map((item) => normalizeJob(item));
}

export async function fetchJobsPage({
  email,
  technicianEmail,
  search,
  page = 1,
  pageSize = 100,
  sortBy = 'datum',
  sortDir = 'desc',
  signal,
} = {}) {
  const normalizedEmail = String(email || '').trim();
  if (!normalizedEmail) {
    return { items: [], total: 0, hasMore: false };
  }

  try {
    const path = getPath('REACT_APP_CRM_API_ZAKAZKY_LIST_PATH', 'protokoly-zakazky-list-entra');
    const query = buildQuery({
      email: normalizedEmail,
      technicianEmail: String(technicianEmail || '').trim(),
      search: String(search || '').trim(),
      page,
      pageSize,
      sortBy,
      sortDir,
      includeTotal: true,
    });
    const raw = await request(`${path}${query}`, { method: 'GET', signal });
    return unwrapPagedResponse(raw, page, pageSize);
  } catch (error) {
    if (isBadRequest(error) && !String(search || '').trim()) {
      const items = await fetchJobsEnhanced({
        email: normalizedEmail,
        technicianEmail,
        page,
        pageSize,
        sortBy,
        sortDir,
        signal,
      });
      return { items, total: null, hasMore: items.length >= pageSize };
    }
    throw error;
  }
}

async function fetchLegacyJobsByEmail(email) {
  const normalizedEmail = String(email || '').trim();
  if (!normalizedEmail) return [];

  const path = getPath('REACT_APP_CRM_API_ZAKAZKY_LIST_PATH', 'protokoly-zakazky-list-entra');
  const query = buildQuery({ email: normalizedEmail });
  const raw = await request(`${path}${query}`, { method: 'GET' });
  return unwrapListResponse(raw).map((item) => normalizeJob(item));
}

function parseEmailList(value) {
  return String(value || '')
    .split(',')
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean);
}

export async function fetchJobsByEmail(email) {
  const normalizedEmail = String(email || '').trim();
  if (!normalizedEmail) return [];

  try {
    return await fetchJobsEnhanced({
      email: normalizedEmail,
      technicianEmail: normalizedEmail,
      page: 1,
      pageSize: 200,
      sortBy: 'datum',
      sortDir: 'desc',
    });
  } catch (error) {
    if (isBadRequest(error)) {
      return fetchLegacyJobsByEmail(normalizedEmail);
    }
    throw error;
  }
}

export async function fetchJobById({ email, zakazkaId, signal } = {}) {
  const normalizedEmail = String(email || '').trim();
  const numericId = Number(zakazkaId);
  if (!normalizedEmail || !Number.isFinite(numericId) || numericId <= 0) return null;

  const jobId = Math.trunc(numericId);
  const path = getPath('REACT_APP_CRM_API_ZAKAZKY_LIST_PATH', 'protokoly-zakazky-list-entra');
  const query = buildQuery({
    email: normalizedEmail,
    zakazkaId: jobId,
    page: 1,
    pageSize: 1,
    includeTotal: false,
  });
  const raw = await request(`${path}${query}`, { method: 'GET', signal });
  const jobs = unwrapListResponse(raw).map((item) => normalizeJob(item));
  return jobs.find((item) => Number(item.ZakazkaId) === jobId) || null;
}

export async function fetchJobProtocol({ zakazkaId, signal } = {}) {
  const path = getPath('REACT_APP_CRM_API_ZAKAZKY_LIST_PATH', 'protokoly-zakazky-list-entra');
  return request(`${path}${buildQuery({ zakazkaId, protocolPreview: true, page: 1, pageSize: 1 })}`, { method: 'GET', signal });
}

export async function fetchAllJobs(currentUserEmail) {
  const normalizedEmail = String(currentUserEmail || '').trim();

  try {
    return await fetchJobsEnhanced({
      email: normalizedEmail,
      page: 1,
      pageSize: 200,
      sortBy: 'datum',
      sortDir: 'desc',
    });
  } catch (error) {
    if (isForbidden(error)) {
      return fetchJobsByEmail(normalizedEmail);
    }
    if (isBadRequest(error)) {
      return fetchJobsByEmail(normalizedEmail);
    }
    throw error;
  }
}

export async function fetchJobsByEmails(emails) {
  // Deprecated strategy kept for compatibility with existing callers.
  // Enhanced API no longer needs client-side multi-email fan-out.
  if (emails && emails.length) {
    return fetchAllJobs(emails[0]);
  }
  return [];
}

export async function fetchAllJobsForScope(options = {}) {
  const configuredEmails = parseEmailList(ENV.REACT_APP_CRM_ALL_TECH_EMAILS);
  const optionEmails = parseEmailList(options.emails);
  const hasAllHint = configuredEmails.length > 0 || optionEmails.length > 0;

  if (hasAllHint || options.forceAll) {
    try {
      return await fetchAllJobs(options.currentUserEmail);
    } catch (error) {
      if (isForbidden(error)) {
        return fetchJobsByEmail(options.currentUserEmail);
      }
      throw error;
    }
  }

  // Fallback to current user scope if caller did not request all-scope explicitly.
  return fetchJobsByEmail(options.currentUserEmail);
}

export async function fetchCustomers() {
  const path = getPath('REACT_APP_CRM_API_ZAKAZNICI_LIST_PATH', 'protokoly-customers-entra');
  const raw = await request(path, { method: 'GET' });
  const list = Array.isArray(raw) ? raw : [];
  return list.map((item) => normalizeCustomer(item)).filter((c) => c.ZakaznikId > 0);
}

export async function fetchUnpaidInvoices({ includeOld = false, signal } = {}) {
  const path = getPath('REACT_APP_CRM_API_VYFAKTURUJ_UNPAID_PATH', 'vyfakturuj-neuhrazene-entra');
  const raw = await request(`${path}${buildQuery({ includeOld: includeOld ? 'true' : '' })}`, { method: 'GET', signal });
  return {
    items: unwrapListResponse(raw),
  };
}

export async function checkInsolvencyByIco({ ico, signal } = {}) {
  const normalizedIco = String(ico || '').replace(/\D/g, '');
  if (!/^\d{8}$/.test(normalizedIco)) throw new Error('Pro kontrolu insolvence chybí platné IČ.');
  const path = getPath('REACT_APP_CRM_API_INSOLVENCY_PATH', 'insolvence-entra');
  return request(`${path}${buildQuery({ ico: normalizedIco })}`, { method: 'GET', signal });
}

export async function saveJob(payload) {
  const path = getPath('REACT_APP_CRM_API_ZAKAZKY_PATH', 'protokoly-zakazka-entra');
  const method = payload && payload.ZakazkaId ? 'PUT' : 'POST';
  return request(path, {
    method,
    body: JSON.stringify(payload),
  });
}

export async function deleteJobById(zakazkaId) {
  const path = getPath('REACT_APP_CRM_API_ZAKAZKY_PATH', 'protokoly-zakazka-entra');
  await request(`${path}?ZakazkaId=${encodeURIComponent(String(zakazkaId))}`, {
    method: 'DELETE',
    body: JSON.stringify({ ZakazkaId: zakazkaId }),
  });
}

export async function createCustomer(payload) {
  const path = getPath('REACT_APP_CRM_API_ZAKAZNICI_PATH', 'protokoly-zakaznik-entra');
  const raw = await request(path, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  return normalizeCustomer(raw);
}

export async function updateCustomer(payload) {
  const path = getPath('REACT_APP_CRM_API_ZAKAZNICI_PATH', 'protokoly-zakaznik-entra');
  const raw = await request(path, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  return normalizeCustomer(raw);
}

export async function deleteCustomerById(zakaznikId) {
  const path = getPath('REACT_APP_CRM_API_ZAKAZNICI_PATH', 'protokoly-zakaznik-entra');
  await request(`${path}?ZakaznikId=${encodeURIComponent(String(zakaznikId))}`, {
    method: 'DELETE',
    body: JSON.stringify({ ZakaznikId: zakaznikId }),
  });
}

function toNullableNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function normalizeProvizeRow(input) {
  const source = input || {};
  return {
    ZakazkaId: Number(source.ZakazkaId || source.zakazkaId || source.ID || 0),
    ZamestnanecId: Number(source.ZamestnanecId || source.zamestnanecId || 0),
    Zamestnanec: String(source.Zamestnanec || source.zamestnanec || ''),
    Rok: Number(source.Rok || source.rok || 0),
    Mesic: Number(source.Mesic || source.mesic || 0),
    PocetZakazek: Number(source.PocetZakazek || source.pocetZakazek || 0),
    Zakaznik: String(source.Zakaznik || source.NazevZakaznika || source.nazevZakaznika || ''),
    Firma: String(source.Firma || source.FirmaNazev || source.firma || source.firmaNazev || ''),
    Skudce: String(source.Skudce || source.SkudceNazev || source.skudce || source.skudceNazev || ''),
    Datum: String(source.Datum || source.datum || source.DatumZakazky || source.datumZakazky || ''),
    CenaZakazky: toNullableNumber(source.CenaZakazky || source.cenaZakazky),
    CenaMaterial: toNullableNumber(source.CenaMaterial || source.cenaMaterial),
    OstatniNaklady: toNullableNumber(source.OstatniNaklady || source.ostatniNaklady),
    CenaDoprava: toNullableNumber(source.CenaDoprava || source.cenaDoprava || source.Doprava),
    DPH: toNullableNumber(source.DPH || source.dph),
    Dan: toNullableNumber(source.Dan || source.dan),
    Zisk: toNullableNumber(source.Zisk || source.zisk),
    Provize_ZAM: toNullableNumber(source.Provize_ZAM || source.provize_ZAM || source.ProvizeZam || source.provizeZam),
    Provize_FA_ZAM: toNullableNumber(source.Provize_FA_ZAM || source.provize_FA_ZAM),
    Provize_Hotovost_ZAM: toNullableNumber(source.Provize_Hotovost_ZAM || source.provize_Hotovost_ZAM),
    Provize_Derator: toNullableNumber(source.Provize_Derator || source.provize_Derator),
    Provize_FA_Derator: toNullableNumber(source.Provize_FA_Derator || source.provize_FA_Derator),
    KcHod: toNullableNumber(source.KcHod || source.kcHod || source.KcHodina),
  };
}

export async function fetchProvizeZamestnanec({ zamestnanecId, year, month, detail = 0, email, signal } = {}) {
  const employeeId = Number(zamestnanecId);
  const y = Number(year);
  const m = Number(month);
  const d = Number(detail);

  if (!Number.isInteger(employeeId) || employeeId <= 0) {
    throw new Error('Parametr zamestnanecId je povinný a musí být celé číslo.');
  }
  if (!Number.isInteger(y) || y < 2000 || y > 2100) {
    throw new Error('Parametr year je povinný a musí být celé číslo.');
  }
  if (!Number.isInteger(m) || m < 1 || m > 12) {
    throw new Error('Parametr month je povinný a musí být číslo 1 až 12.');
  }
  if (!(d === 0 || d === 1)) {
    throw new Error('Parametr detail může být jen 0 nebo 1.');
  }

  const path = getPath('REACT_APP_CRM_API_PROVIZE_ZAMESTNANEC_PATH', 'provize-zamestnanec-entra');
  const query = buildQuery({
    zamestnanecId: employeeId,
    year: y,
    month: m,
    detail: d,
    email: String(email || '').trim(),
  });
  const raw = await request(`${path}${query}`, { method: 'GET', signal });
  return unwrapListResponse(raw).map((item) => normalizeProvizeRow(item));
}

export async function fetchCurrentUserAccess({ email, signal } = {}) {
  const normalizedEmail = String(email || '').trim();
  if (!normalizedEmail) return { canViewAllJobs: false };

  const result = await fetchJobsPage({
    email: normalizedEmail,
    page: 1,
    pageSize: 1,
    sortBy: 'datum',
    sortDir: 'desc',
    signal,
  });

  return {
    canViewAllJobs: Boolean(result.canViewAll),
  };
}

export async function fetchRegularInspections({ email, status, fromDate, toDate, signal } = {}) {
  const path = getPath('REACT_APP_CRM_API_PRAVIDELNE_KONTROLY_PATH', 'pravidelne-kontroly-entra');
  const query = buildQuery({
    email: String(email || '').trim(),
    status: String(status || '').trim(),
    fromDate: String(fromDate || '').trim(),
    toDate: String(toDate || '').trim(),
  });

  const raw = await request(`${path}${query}`, { method: 'GET', signal });
  return unwrapListResponse(raw).map((item) => normalizeRegularInspection(item));
}

export async function rescheduleRegularInspection({ ZakaznikId, EAN, PlannedDate, ZamestnanecId, Aktivni } = {}) {
  const path = getPath('REACT_APP_CRM_API_PRAVIDELNE_KONTROLY_PATH', 'pravidelne-kontroly-entra');
  return request(path, {
    method: 'POST',
    body: JSON.stringify({ ZakaznikId, EAN: EAN || null, PlannedDate, ZamestnanecId, Aktivni }),
  });
}

function asString(value) {
  if (value === null || value === undefined) return '';
  return String(value).trim();
}

function toNullableWholeNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(String(value).replace(',', '.').trim());
  if (!Number.isFinite(n)) return null;
  return Math.round(n);
}

function normalizeAiJobDraft(raw) {
  const source = raw && typeof raw === 'object' ? raw : {};
  const first = Array.isArray(source.items) && source.items.length > 0 ? source.items[0] : null;
  const data = first && typeof first === 'object' ? first : source;

  return {
    SessionId: asString(data.SessionId || data.sessionId),
    AssistantText: asString(data.AssistantText || data.assistantText),
    MissingFields: Array.isArray(data.MissingFields || data.missingFields) ? (data.MissingFields || data.missingFields) : [],
    IsComplete: Boolean(data.IsComplete || data.isComplete),
    ZakaznikId: toNullableWholeNumber(data.ZakaznikId || data.zakaznikId || data.customerId),
    ZakaznikNazev: asString(data.ZakaznikNazev || data.zakaznikNazev || data.customerName),
    Datum: asString(data.Datum || data.datum || data.date),
    CenaZakazky: toNullableWholeNumber(data.CenaZakazky || data.cenaZakazky || data.price),
    OstatniNaklady: toNullableWholeNumber(data.OstatniNaklady || data.ostatniNaklady || data.otherCosts),
    DopravaKm: toNullableWholeNumber(data.DopravaKm || data.dopravaKm || data.Doprava || data.doprava || data.km),
    SkudceId: toNullableWholeNumber(data.SkudceId || data.skudceId || data.pestId),
    FirmaId: toNullableWholeNumber(data.FirmaId || data.firmaId || data.companyId),
    ZpusobPlatbyId: toNullableWholeNumber(data.ZpusobPlatbyId || data.zpusobPlatbyId || data.paymentId),
    CisloProtokolu: toNullableWholeNumber(data.CisloProtokolu || data.cisloProtokolu || data.protocolNumber),
    Poznamka: asString(data.Poznamka || data.poznamka || data.note || data.remark),
    Raw: data,
  };
}

function normalizeAiCustomerCandidate(input) {
  const source = input && typeof input === 'object' ? input : {};
  return {
    ZakaznikId: toNullableWholeNumber(source.ZakaznikId || source.zakaznikId || source.customerId),
    Nazev: asString(source.Nazev || source.nazev || source.customerName),
    Ulice: asString(source.Ulice || source.ulice || source.street),
    KontaktniOsoba: asString(source.KontaktniOsoba || source.kontaktniOsoba || source.contactName),
    Telefon: asString(source.Telefon || source.telefon || source.phone),
    Email: asString(source.Email || source.email),
  };
}

function normalizeAiJobAgentState(raw) {
  const source = raw && typeof raw === 'object' ? raw : {};
  return {
    SessionId: asString(source.SessionId || source.sessionId),
    ConfirmationToken: asString(source.ConfirmationToken),
    ZakaznikUlice: asString(source.ZakaznikUlice),
    AssistantText: asString(source.AssistantText || source.assistantText),
    MissingFields: Array.isArray(source.MissingFields || source.missingFields) ? (source.MissingFields || source.missingFields) : [],
    IsComplete: Boolean(source.IsComplete || source.isComplete),
    CanSave: Boolean(source.CanSave || source.canSave),
    NeedsCustomerSelection: Boolean(source.NeedsCustomerSelection || source.needsCustomerSelection),
    CustomerCandidates: Array.isArray(source.CustomerCandidates || source.customerCandidates)
      ? (source.CustomerCandidates || source.customerCandidates).map((item) => normalizeAiCustomerCandidate(item)).filter((item) => item.ZakaznikId > 0)
      : [],
    SavedJob: source.SavedJob && typeof source.SavedJob === 'object'
      ? {
          ZakazkaId: toNullableWholeNumber(source.SavedJob.ZakazkaId || source.SavedJob.zakazkaId),
        }
      : null,
    ZakaznikId: toNullableWholeNumber(source.ZakaznikId || source.zakaznikId || source.customerId),
    ZakaznikNazev: asString(source.ZakaznikNazev || source.zakaznikNazev || source.customerName),
    Datum: asString(source.Datum || source.datum || source.date),
    CenaZakazky: toNullableWholeNumber(source.CenaZakazky ?? source.cenaZakazky ?? source.price),
    OstatniNaklady: toNullableWholeNumber(source.OstatniNaklady ?? source.ostatniNaklady ?? source.otherCosts),
    DopravaKm: toNullableWholeNumber(source.DopravaKm ?? source.dopravaKm ?? source.km),
    SkudceId: toNullableWholeNumber(source.SkudceId || source.skudceId || source.pestId),
    FirmaId: toNullableWholeNumber(source.FirmaId || source.firmaId || source.companyId),
    ZpusobPlatbyId: toNullableWholeNumber(source.ZpusobPlatbyId || source.zpusobPlatbyId || source.paymentId),
    CisloProtokolu: toNullableWholeNumber(source.CisloProtokolu ?? source.cisloProtokolu ?? source.protocolNumber),
    Poznamka: asString(source.Poznamka || source.poznamka || source.note || source.remark),
    Raw: source,
  };
}

export async function fetchAiJobDraft({ transcript, email, sessionId, signal } = {}) {
  const text = asString(transcript);
  if (!text) {
    throw new Error('Text vstupu pro AI je prázdný.');
  }

  const path = getPath('REACT_APP_CRM_API_AI_VOICE_DRAFT_PATH', 'ai-hlas-zakazka-entra');
  const raw = await request(path, {
    method: 'POST',
    signal,
    body: JSON.stringify({
      transcript: text,
      email: asString(email),
      sessionId: asString(sessionId),
      source: 'react-crm',
    }),
  });

  return normalizeAiJobDraft(raw);
}

async function postAiJobAgentAction(body, signal) {
  const path = getPath('REACT_APP_CRM_API_AI_AGENT_PATH', 'ai-hlas-zakazka-agent-entra');
  const raw = await request(path, {
    method: 'POST',
    signal,
    body: JSON.stringify(body),
  });
  return normalizeAiJobAgentState(raw);
}

export async function fetchAiJobAgentRealtimeSession({ signal } = {}) {
  const path = getPath('REACT_APP_CRM_API_AI_AGENT_SESSION_PATH', 'ai-hlas-zakazka-agent-entra-session');
  const raw = await request(path, {
    method: 'POST',
    signal,
    body: JSON.stringify({ source: 'react-crm-realtime' }),
  });

  return raw && typeof raw === 'object' ? raw : null;
}

export async function uploadScanProtocolFile({ file, signal } = {}) {
  if (!(file instanceof File)) {
    throw new Error('Soubor pro scan chybí.');
  }

  const path = getPath('REACT_APP_CRM_API_SCAN_PROTOCOL_UPLOAD_PATH', 'upload-scan-protokoly-blob-entra');
  const formData = new FormData();
  formData.append('file', file, file.name || 'scan.jpg');

  const raw = await request(path, {
    method: 'POST',
    signal,
    body: formData,
  });

  return raw && typeof raw === 'object' ? raw : null;
}

export async function ingestScanProtocol({ blobPath, blobUrl, capturedAt, latitude, longitude, zamestnanecId, signal } = {}) {
  const path = getPath('REACT_APP_CRM_API_SCAN_PROTOCOL_AI_PATH', 'scan-protokoly-ai-entra');
  const raw = await request(path, {
    method: 'POST',
    signal,
    body: JSON.stringify({
      blobPath: asString(blobPath),
      blobUrl: asString(blobUrl),
      capturedAt: asString(capturedAt),
      latitude: latitude ?? null,
      longitude: longitude ?? null,
      zamestnanecId: zamestnanecId ?? null,
    }),
  });

  return raw && typeof raw === 'object' ? raw : null;
}

export async function enqueueScanProtocol({ blobPath, blobUrl, capturedAt, capturedLocalDate, scanMode, jobDate, latitude, longitude, zamestnanecId, guid, signal } = {}) {
  const path = getPath('REACT_APP_CRM_API_SCAN_PROTOCOL_ENQUEUE_PATH', 'enqueue-scan-protokoly-entra');
  const raw = await request(path, {
    method: 'POST',
    signal,
    body: JSON.stringify({
      blobPath: asString(blobPath),
      blobUrl: asString(blobUrl),
      capturedAt: asString(capturedAt),
      capturedLocalDate,
      scanMode,
      jobDate,
      latitude: latitude ?? null,
      longitude: longitude ?? null,
      zamestnanecId: zamestnanecId ?? null,
      guid: asString(guid),
    }),
  });

  return raw && typeof raw === 'object' ? raw : null;
}

export async function fetchScanProtocolQueue({ limit = 50, ids = [], signal } = {}) {
  const path = getPath('REACT_APP_CRM_API_SCAN_PROTOCOL_QUEUE_LIST_PATH', 'scan-protokoly-queue-list-entra');
  const normalizedIds = Array.isArray(ids)
    ? ids.map((item) => Number(item)).filter((item) => Number.isFinite(item) && item > 0)
    : []
  const query = buildQuery({ limit, ids: normalizedIds.length ? normalizedIds.join(',') : '' });
  const raw = await request(`${path}${query}`, {
    method: 'GET',
    signal,
  });

  return unwrapListResponse(raw);
}

export async function kickScanProtocolProcessing({ limit = 3, ids = [], signal } = {}) {
  const path = getPath('REACT_APP_CRM_API_SCAN_PROTOCOL_PROCESS_PATH', 'scan-protokoly-process-pending-entra');
  const normalizedIds = Array.isArray(ids)
    ? ids.map((item) => Number(item)).filter((item) => Number.isFinite(item) && item > 0)
    : [];
  const raw = await request(path, {
    method: 'POST',
    signal,
    body: JSON.stringify({
      limit,
      ids: normalizedIds,
    }),
  });

  return raw && typeof raw === 'object' ? raw : null;
}

export async function assignScanProtocolToJob({ scanId, zakaznikId, datum, targetJobId, createNew, corrections, signal } = {}) {
  const path = getPath('REACT_APP_CRM_API_SCAN_PROTOCOL_MANAGE_PATH', 'scan-protokoly-manage-entra');
  const raw = await request(path, {
    method: 'POST',
    signal,
    body: JSON.stringify({
      scanId,
      zakaznikId,
      datum,
      targetJobId,
      createNew,
      corrections,
    }),
  });

  return raw && typeof raw === 'object' ? raw : null;
}

export async function fetchScanJobCandidates({ scanId, zakaznikId, datum, protocolNumber, signal } = {}) {
  const path = getPath('REACT_APP_CRM_API_SCAN_PROTOCOL_MANAGE_PATH', 'scan-protokoly-manage-entra');
  return unwrapListResponse(await request(`${path}${buildQuery({ scanId, zakaznikId, datum })}&protocolNumber=${encodeURIComponent(protocolNumber || '')}`, { method: 'GET', signal }));
}

export async function retryScanProtocol({ scanId, signal } = {}) {
  const path = getPath('REACT_APP_CRM_API_SCAN_PROTOCOL_MANAGE_PATH', 'scan-protokoly-manage-entra');
  return request(path, { method: 'POST', signal, body: JSON.stringify({ action: 'retry', scanId }) });
}

export async function deleteScanProtocol({ scanId, signal } = {}) {
  const path = getPath('REACT_APP_CRM_API_SCAN_PROTOCOL_MANAGE_PATH', 'scan-protokoly-manage-entra');
  const raw = await request(`${path}?scanId=${encodeURIComponent(String(scanId || ''))}`, {
    method: 'DELETE',
    signal,
    body: JSON.stringify({ scanId }),
  });

  return raw && typeof raw === 'object' ? raw : null;
}

export async function updateAiJobAgentDraft({ sessionId, fields, email, signal } = {}) {
  return postAiJobAgentAction({
    action: 'update_draft',
    sessionId: asString(sessionId),
    email: asString(email),
    fields: fields && typeof fields === 'object' ? fields : {},
    source: 'react-crm-realtime',
  }, signal);
}

export async function runAiJobAgentTurn({ transcript, email, sessionId, signal } = {}) {
  const text = asString(transcript);
  if (!text) {
    throw new Error('Text vstupu pro AI agenta je prázdný.');
  }

  return postAiJobAgentAction({
    action: 'turn',
    transcript: text,
    email: asString(email),
    sessionId: asString(sessionId),
    source: 'react-crm-agent',
  }, signal);
}

export async function selectAiJobAgentCustomer({ customerId, email, sessionId, signal } = {}) {
  const normalizedCustomerId = toNullableWholeNumber(customerId);
  if (!normalizedCustomerId) {
    throw new Error('Vybraný zákazník pro AI agenta chybí.');
  }

  return postAiJobAgentAction({
    action: 'select_customer',
    customerId: normalizedCustomerId,
    email: asString(email),
    sessionId: asString(sessionId),
    source: 'react-crm-agent',
  }, signal);
}

export async function searchAiJobAgentCustomer({ query, email, sessionId, signal } = {}) {
  const normalizedQuery = asString(query);
  if (!normalizedQuery) {
    throw new Error('Dotaz pro vyhledání zákazníka chybí.');
  }

  return postAiJobAgentAction({
    action: 'search_customer',
    query: normalizedQuery,
    email: asString(email),
    sessionId: asString(sessionId),
    source: 'react-crm-realtime',
  }, signal);
}

export async function prepareAiJobAgentSave({ sessionId, signal } = {}) {
  return postAiJobAgentAction({ action: 'prepare_save', sessionId: asString(sessionId) }, signal);
}

export async function resumeAiJobAgentDraft({ sessionId, signal } = {}) {
  return postAiJobAgentAction({ action: 'resume', sessionId: asString(sessionId) }, signal);
}

export async function saveAiJobAgentDraft({ email, sessionId, technicianId, confirmationToken, confirmed = false, signal } = {}) {
  const normalizedTechnicianId = toNullableWholeNumber(technicianId);

  return postAiJobAgentAction({
    action: 'save',
    confirmed,
    confirmationToken: asString(confirmationToken),
    email: asString(email),
    technicianEmail: asString(email),
    technicianId: normalizedTechnicianId,
    sessionId: asString(sessionId),
    source: 'react-crm-agent',
  }, signal);
}
